import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react";
import ReactDOM from "react-dom";
import useStockWishlistModel from "./StockWishlistModel";
import Navigation from "../components/Navigation/Navigation";
import UpstoxTokenStatus from "../components/UpstoxTokenStatus";
import StockChart from "../StocksChart/stockChart";
import { motion, AnimatePresence } from "framer-motion";

const SIGNAL_TYPES = {
  ALL: "ALL",
  BUY: "BUY",
  SELL: "SELL",
  NEUTRAL: "NEUTRAL",
};

export default function StockWishlist() {
  const {
    state,
    updateState,
    searchStocks,
    addToWishlist,
    viewStockData,
    clearSelectedStock,
    fetchWatchlist,
    removeFromWishlist,
    removeMultipleFromWishlist,
  } = useStockWishlistModel();
  const [searchTerm, setSearchTerm] = useState("");
  const [signalFilter, setSignalFilter] = useState(SIGNAL_TYPES.ALL);
  const [showSignalFilter, setShowSignalFilter] = useState(false);
  const filterBtnRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  useEffect(() => {
    fetchWatchlist();
  }, [fetchWatchlist]);

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchTerm(query);
    searchStocks(query);
  };

  const handleSelectStock = (stock) => {
    addToWishlist(stock);
    setSearchTerm("");
    updateState({ suggestions: [] });
  };

  const filteredRecommendations = useMemo(() => {
    return state.recommendations.filter((stock) => {
      const matchesSignal =
        signalFilter === SIGNAL_TYPES.ALL || stock.signal === signalFilter;
      // You can add a search filter for the recommendations table if needed
      return matchesSignal;
    });
  }, [state.recommendations, signalFilter]);

  const handleFilterChange = (newFilter) => {
    setSignalFilter(newFilter);
    setShowSignalFilter(false);
  };

  useEffect(() => {
    if (showSignalFilter && filterBtnRef.current) {
      const rect = filterBtnRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 8,
        left: rect.right - 224,
        width: rect.width,
      });
    }
  }, [showSignalFilter]);
  const handleDeleteAll = useCallback(async () => {
    if (state.wishlist.length === 0) return;
    await removeMultipleFromWishlist(state.wishlist);
  }, [removeMultipleFromWishlist, state.wishlist]);

  const currentStockIndex = useMemo(() => {
    if (!state.selectedStock) return -1;
    return filteredRecommendations.findIndex(
      (stock) => stock.symbol === state.selectedStock.symbol,
    );
  }, [state.selectedStock, filteredRecommendations]);

  const handlePreviousStock = useCallback(() => {
    if (currentStockIndex > 0) {
      viewStockData(filteredRecommendations[currentStockIndex - 1]);
    }
  }, [currentStockIndex, filteredRecommendations, viewStockData]);

  const handleNextStock = useCallback(() => {
    if (currentStockIndex < filteredRecommendations.length - 1) {
      viewStockData(filteredRecommendations[currentStockIndex + 1]);
    }
  }, [currentStockIndex, filteredRecommendations, viewStockData]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "ArrowLeft") handlePreviousStock();
      if (event.key === "ArrowRight") handleNextStock();
    };
    if (state.selectedStock) {
      document.addEventListener("keydown", handleKeyDown);
    }
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [state.selectedStock, handlePreviousStock, handleNextStock]);

  return (
    <div className="min-h-screen bg-[#0B1121] text-gray-100">
      <Navigation />
      <div className="p-4 md:p-8">
        <UpstoxTokenStatus />
        <div className="max-w-5xl mx-auto space-y-8">
          <AnimatePresence>
            {state.selectedStock && (
              <div
                className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm"
                onClick={clearSelectedStock}
              >
                <motion.div
                  className="bg-gray-800/40 rounded-2xl overflow-hidden backdrop-blur-xl border border-gray-700/50 shadow-lg w-full max-w-4xl max-h-[80vh] overflow-y-auto"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="p-8 relative">
                    <div className="flex justify-between items-center mb-6">
                      <h2 className="text-xl font-semibold text-gray-100">
                        {state.selectedStock.symbol} Chart
                      </h2>
                      <button
                        onClick={clearSelectedStock}
                        className="text-gray-400 hover:text-gray-200 z-10"
                      >
                        Close
                      </button>
                    </div>
                    <StockChart
                      symbol={state.selectedStock.symbol}
                      name={state.selectedStock.name}
                    />
                  </div>
                  <button
                    onClick={handlePreviousStock}
                    disabled={currentStockIndex <= 0}
                    className="absolute left-2 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-gray-800/50 hover:bg-gray-700/70 disabled:opacity-30 disabled:cursor-not-allowed transition-opacity"
                  >
                    <svg
                      className="w-6 h-6 text-gray-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={handleNextStock}
                    disabled={
                      currentStockIndex >= filteredRecommendations.length - 1
                    }
                    className="absolute right-2 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-gray-800/50 hover:bg-gray-700/70 disabled:opacity-30 disabled:cursor-not-allowed transition-opacity"
                  >
                    <svg
                      className="w-6 h-6 text-gray-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                </motion.div>
              </div>
            )}
          </AnimatePresence>

          <motion.div
            className="bg-gray-800/40 rounded-2xl p-6 backdrop-blur-xl border border-gray-700/50 shadow-xl ring-1 ring-gray-700/50 overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            <div className="text-center mb-6">
              <img
                src="/My_Logo_2.png"
                alt="StockAdi Logo"
                className="h-24 w-24 rounded-full object-cover mx-auto mb-4"
              />
              <div className="flex items-center justify-center gap-3">
                <h2 className="text-xl font-semibold text-gray-100">
                  My Stock Wishlist
                </h2>
                <button
                  onClick={fetchWatchlist}
                  disabled={state.isLoading}
                  className={`p-2 rounded-full transition-all ${
                    state.isLoading
                      ? "text-gray-600 cursor-not-allowed"
                      : "text-blue-400 hover:bg-blue-500/10 hover:text-blue-300"
                  }`}
                  title="Refresh Wishlist"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-5 w-5 ${state.isLoading ? "animate-spin" : ""}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                </button>
                <button
                  onClick={handleDeleteAll}
                  disabled={state.wishlist.length === 0}
                  className={`p-2 rounded-full transition-all ${
                    state.wishlist.length === 0
                      ? "text-gray-600 cursor-not-allowed"
                      : "text-red-400 hover:bg-red-500/10 hover:text-red-300"
                  }`}
                  title={
                    state.wishlist.length === 0
                      ? "No stocks to delete"
                      : "Delete all wishlist stocks"
                  }
                >
                  Delete All
                </button>
              </div>
            </div>

            <div className="flex justify-center">
              <div className="relative mb-4 w-full max-w-md">
                <input
                  type="text"
                  className="w-full px-4 py-2 rounded-lg bg-gray-900/80 border border-gray-700 text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                  placeholder="Search to add a stock to your wishlist..."
                  value={searchTerm}
                  onChange={handleSearch}
                  autoComplete="off"
                />
                {state.suggestions.length > 0 && (
                  <ul className="absolute z-50 w-full mt-1 bg-gray-800 border border-gray-700 rounded-lg shadow-lg">
                    {state.suggestions.map((stock) => (
                      <li
                        key={stock.symbol}
                        className="px-4 py-2 cursor-pointer hover:bg-gray-700"
                        onClick={() => handleSelectStock(stock)}
                      >
                        {stock.name} ({stock.symbol})
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="overflow-x-auto relative rounded-xl">
              <table className="min-w-full divide-y divide-gray-700/50">
                <thead className="bg-gray-900/80 backdrop-blur-sm sticky top-0 z-10">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-blue-300 uppercase tracking-wider">
                      Stock Name
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-blue-300 uppercase tracking-wider">
                      Current Price
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-blue-300 uppercase tracking-wider">
                      <div className="flex items-center justify-center">
                        Signal
                        <button
                          ref={filterBtnRef}
                          onClick={() => setShowSignalFilter(!showSignalFilter)}
                          className="ml-2 p-1 rounded-md hover:bg-gray-700/50"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                            />
                          </svg>
                        </button>
                        {showSignalFilter &&
                          ReactDOM.createPortal(
                            <div
                              id="signal-filter-dropdown"
                              className="fixed w-56 bg-gray-800 rounded-lg shadow-2xl z-[9999] border border-gray-700"
                              style={{
                                top: dropdownPos.top,
                                left: dropdownPos.left,
                              }}
                            >
                              {Object.values(SIGNAL_TYPES).map((type) => (
                                <button
                                  key={type}
                                  onClick={() => handleFilterChange(type)}
                                  className={`w-full text-left px-4 py-2 text-sm ${
                                    signalFilter === type
                                      ? "bg-purple-500/20 text-purple-300"
                                      : "text-gray-300"
                                  } hover:bg-gray-700/50`}
                                >
                                  {type}
                                </button>
                              ))}
                            </div>,
                            document.body,
                          )}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-blue-300 uppercase tracking-wider">
                      Historical Data
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-blue-300 uppercase tracking-wider">
                      Buy Range
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-blue-300 uppercase tracking-wider">
                      Sell Range
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-blue-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-gray-800/50 divide-y divide-gray-700">
                  {filteredRecommendations.length > 0 ? (
                    filteredRecommendations.map((stock) => (
                      <motion.tr
                        key={stock.symbol}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3 }}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-100">
                            {stock.symbol}
                          </div>
                          <div className="text-sm text-gray-400">
                            {stock.name}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-300">
                          {stock.currentPrice != null ? `₹${Number(stock.currentPrice).toFixed(2)}` : '—'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span
                            className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium ${
                              stock.signal === "BUY"
                                ? "bg-green-500/20 text-green-300"
                                : stock.signal === "SELL"
                                  ? "bg-red-500/20 text-red-300"
                                  : "bg-gray-500/20 text-gray-400"
                            }`}
                          >
                            {stock.signal}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <button
                            onClick={() => viewStockData(stock)}
                            className="text-blue-400 hover:text-blue-300 text-sm font-medium"
                          >
                            View Chart
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-300">
                          {stock.predictedBuyRange && stock.predictedBuyRange.min
                            ? `₹${stock.predictedBuyRange.min.toFixed(
                                2,
                              )} - ₹${stock.predictedBuyRange.max.toFixed(2)}`
                            : '—'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-300">
                          {stock.predictedSellRange && stock.predictedSellRange.min
                            ? `₹${stock.predictedSellRange.min.toFixed(
                                2,
                              )} - ₹${stock.predictedSellRange.max.toFixed(2)}`
                            : '—'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeFromWishlist(stock);
                            }}
                            className="p-2 rounded-full hover:bg-red-500/10 transition-colors group"
                            title="Remove from wishlist"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-5 w-5 text-red-500 group-hover:text-red-400"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </button>
                        </td>
                      </motion.tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="7"
                        className="px-6 py-8 text-center text-gray-400"
                      >
                        {state.wishlist.length === 0
                          ? "Search for stocks to add to your wishlist."
                          : "Your wishlist is empty or not yet analyzed."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
